import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FloppyDisk, Trash, ArrowClockwise } from "@phosphor-icons/react";

// Cash Position History — Finance / Admin backfill screen.
// Lists every stored daily snapshot in the picked date range PLUS every
// missing day so historical `additional_fund` values can be typed in for
// past periods. Editing any cell marks the row dirty; per-row Save upserts
// via PUT /settings/cash-position (which is idempotent).

function today() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}
function enumerateDates(startYmd, endYmd) {
  const out = [];
  if (!startYmd || !endYmd) return out;
  const cur = new Date(startYmd + "T00:00:00Z");
  const stop = new Date(endYmd + "T00:00:00Z");
  if (cur > stop) return out;
  while (cur <= stop) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

export default function CashPositionHistoryPage() {
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [rows, setRows] = useState({}); // { as_of_date: { closing_balance, additional_fund, additional_fund_from, additional_fund_liq, updated_by_name, updated_at, _dirty } }
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState(null);
  const [savingAll, setSavingAll] = useState(false);

  async function load() {
    if (!startDate || !endDate) return;
    setLoading(true);
    try {
      const { data } = await api.get("/settings/cash-positions", {
        params: { start_date: startDate, end_date: endDate },
      });
      // Merge fetched snapshots into a keyed map so unfetched days remain blank.
      const map = {};
      for (const d of data || []) map[d.as_of_date] = { ...d, _dirty: false };
      setRows(map);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load snapshots");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  const dates = useMemo(() => enumerateDates(startDate, endDate).reverse(), [startDate, endDate]);

  function setCell(date, patch) {
    setRows((prev) => {
      const cur = prev[date] || { as_of_date: date, closing_balance: "", additional_fund: 0, additional_fund_from: "" };
      return { ...prev, [date]: { ...cur, ...patch, _dirty: true } };
    });
  }

  async function saveOne(date) {
    const row = rows[date] || { closing_balance: 0, additional_fund: 0, additional_fund_from: "" };
    setSavingKey(date);
    try {
      const payload = {
        as_of_date: date,
        closing_balance: Number(row.closing_balance) || 0,
        closing_set: row.closing_balance !== "" && row.closing_balance !== null && row.closing_balance !== undefined && !(Number(row.closing_balance) === 0 && !row.closing_set),
        additional_fund: Number(row.additional_fund) || 0,
        additional_fund_from: String(row.additional_fund_from || "").trim(),
      };
      const { data } = await api.put("/settings/cash-position", payload);
      setRows((prev) => ({ ...prev, [date]: { ...data, _dirty: false } }));
      toast.success(`Saved ${date}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSavingKey(null);
    }
  }

  async function saveAllDirty() {
    const dirtyDates = Object.entries(rows).filter(([, v]) => v?._dirty).map(([k]) => k);
    if (!dirtyDates.length) { toast.info("Nothing to save"); return; }
    setSavingAll(true);
    let ok = 0, fail = 0;
    for (const d of dirtyDates) {
      const row = rows[d];
      try {
        const payload = {
          as_of_date: d,
          closing_balance: Number(row.closing_balance) || 0,
          closing_set: row.closing_balance !== "" && row.closing_balance !== null && row.closing_balance !== undefined && !(Number(row.closing_balance) === 0 && !row.closing_set),
          additional_fund: Number(row.additional_fund) || 0,
          additional_fund_from: String(row.additional_fund_from || "").trim(),
        };
        const { data } = await api.put("/settings/cash-position", payload);
        setRows((prev) => ({ ...prev, [d]: { ...data, _dirty: false } }));
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setSavingAll(false);
    if (fail) toast.warning(`Saved ${ok}, failed ${fail}`);
    else toast.success(`Saved ${ok} snapshot${ok === 1 ? "" : "s"}`);
  }

  async function removeSnapshot(date) {
    if (!window.confirm(`Delete the snapshot for ${date}? Historical reports for this day will read zero again.`)) return;
    try {
      await api.delete(`/settings/cash-position/${date}`);
      setRows((prev) => {
        const next = { ...prev };
        delete next[date];
        return next;
      });
      toast.success(`Removed ${date}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  }

  const dirtyCount = Object.values(rows).filter((r) => r?._dirty).length;

  return (
    <div className="space-y-6" data-testid="cash-position-history-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Finance · Backfill</div>
          <h1 className="section-title text-3xl sm:text-4xl">Cash Position History</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Enter historical <b>Additional Fund</b> top-ups and <b>Closing Balance</b> values so
            weekly / monthly / custom reports stop reading zero for days before the field existed.
            One row per calendar day — leave a row blank to skip it.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4 border border-border rounded-md bg-muted/30">
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border-border"
            data-testid="cph-start"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border-border"
            data-testid="cph-end"
          />
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="border-border" data-testid="cph-reload">
          <ArrowClockwise size={14} className="mr-2" /> {loading ? "Loading…" : "Reload"}
        </Button>
        <div className="flex-1" />
        <Button onClick={saveAllDirty} disabled={savingAll || !dirtyCount} data-testid="cph-save-all">
          <FloppyDisk size={14} className="mr-2" />
          {savingAll ? "Saving…" : `Save all${dirtyCount ? ` (${dirtyCount})` : ""}`}
        </Button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[130px]">Date</TableHead>
              <TableHead className="text-right">Closing Balance (₱)</TableHead>
              <TableHead className="text-right">Additional Fund (₱)</TableHead>
              <TableHead>Additional Fund From</TableHead>
              <TableHead className="text-right">From Liquidation (₱)</TableHead>
              <TableHead>Last Updated</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dates.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">Pick a From / To date to list days.</TableCell></TableRow>
            ) : dates.map((d) => {
              const row = rows[d];
              const stored = !!row?.updated_at;
              const dirty = !!row?._dirty;
              return (
                <TableRow key={d} data-testid={`cph-row-${d}`} className={dirty ? "bg-amber-50/40" : undefined}>
                  <TableCell className="font-mono text-xs tabular-nums">
                    {d}
                    {!stored && !dirty && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">no data</span>
                    )}
                    {dirty && (
                      <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-700">unsaved</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number" step="0.01"
                      value={row?.closing_balance ?? ""}
                      onChange={(e) => setCell(d, { closing_balance: e.target.value })}
                      placeholder="0.00"
                      className="h-8 tabular-nums text-right border-border"
                      data-testid={`cph-closing-${d}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number" step="0.01"
                      value={row?.additional_fund ?? ""}
                      onChange={(e) => setCell(d, { additional_fund: e.target.value })}
                      placeholder="0.00"
                      className="h-8 tabular-nums text-right border-border"
                      data-testid={`cph-addfund-${d}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="text"
                      value={row?.additional_fund_from ?? ""}
                      onChange={(e) => setCell(d, { additional_fund_from: e.target.value })}
                      placeholder="e.g. Owner top-up, BDO transfer"
                      className="h-8 border-border"
                      data-testid={`cph-addfund-from-${d}`}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                    {row?.additional_fund_liq ? Number(row.additional_fund_liq).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {row?.updated_by_name ? `${row.updated_by_name}` : "—"}
                    {row?.updated_at ? <div className="text-[10px]">{String(row.updated_at).slice(0, 16).replace("T", " ")}</div> : null}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant={dirty ? "default" : "outline"}
                        onClick={() => saveOne(d)}
                        disabled={savingKey === d}
                        className={dirty ? "" : "border-border"}
                        data-testid={`cph-save-${d}`}
                      >
                        <FloppyDisk size={14} className="mr-1" /> Save
                      </Button>
                      {stored && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeSnapshot(d)}
                          className="border-border"
                          data-testid={`cph-delete-${d}`}
                        >
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
